import { Button, Card, CardContent, Input, Label, cn } from "@safeplate/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { Delete, UserPlus } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { signInWithEmail, signOut } from "../api/auth.js";
import { setActiveOrganisationHeader } from "../api/client.js";
import { ApiError, displayError } from "../api/errors.js";
import { Brand } from "../components/brand.js";
import { SlowStartNotice } from "../components/slow-start.js";
import { ErrorState, LoadingState } from "../components/states.js";
import {
  initialsOf,
  lockScreenAvailable,
  roleLabels,
  type PersonTile,
} from "./people-on-computer.js";
import { usePeopleOnComputer } from "./people-store.js";
import { PIN_LENGTH } from "./pin.js";
import { unlockSharedDevice } from "./quick-access-api.js";

const PAD_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const;

function PinPad({
  disabled,
  onChange,
  value,
}: {
  disabled: boolean;
  onChange: (next: string) => void;
  value: string;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (disabled || event.metaKey || event.ctrlKey || event.altKey) return;
      if (/^\d$/.test(event.key)) {
        onChange((value + event.key).slice(0, PIN_LENGTH));
      } else if (event.key === "Backspace") {
        onChange(value.slice(0, -1));
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [disabled, onChange, value]);

  const keyClass =
    "grid h-14 place-items-center rounded-xl border border-stone-200 bg-surface text-xl font-bold text-stone-900 transition hover:border-emerald-700 active:bg-emerald-50 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800";

  return (
    <div>
      <div
        aria-label={`${value.length} of ${PIN_LENGTH} digits entered`}
        className="flex justify-center gap-3"
        role="img"
      >
        {Array.from({ length: PIN_LENGTH }, (_, index) => (
          <span
            className={cn(
              "size-3.5 rounded-full border-2 border-emerald-800",
              index < value.length ? "bg-emerald-800" : "bg-transparent",
            )}
            key={index}
          />
        ))}
      </div>
      <div className="mx-auto mt-6 grid max-w-xs grid-cols-3 gap-3">
        {PAD_KEYS.map((digit) => (
          <button
            className={keyClass}
            disabled={disabled}
            key={digit}
            onClick={() => onChange((value + digit).slice(0, PIN_LENGTH))}
            type="button"
          >
            {digit}
          </button>
        ))}
        <span />
        <button
          className={keyClass}
          disabled={disabled}
          onClick={() => onChange((value + "0").slice(0, PIN_LENGTH))}
          type="button"
        >
          0
        </button>
        <button
          aria-label="Delete"
          className={keyClass}
          disabled={disabled || value.length === 0}
          onClick={() => onChange(value.slice(0, -1))}
          type="button"
        >
          <Delete className="size-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export function LockScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const people = usePeopleOnComputer();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  // A PIN that more than one person here uses: they tap their name.
  const [shared, setShared] = useState<{ ids: string[]; pin: string } | null>(
    null,
  );

  const selected: PersonTile | undefined = people.tiles.find(
    (tile) => tile.key === selectedKey,
  );

  async function finish(orgId: string | null) {
    setActiveOrganisationHeader(orgId);
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    await navigate({ to: "/" });
  }

  const unlock = useMutation({
    mutationFn: (input: { pin: string; staffProfileId?: string }) =>
      unlockSharedDevice(input.pin, input.staffProfileId),
    onError: () => setPin(""),
    onSuccess: async (result, input) => {
      if (result.kind === "unlocked") return finish(people.orgId);
      setPin("");
      setSelectedKey(null);
      setShared({ ids: result.staffProfileIds, pin: input.pin });
    },
  });
  const signIn = useMutation({
    mutationFn: () => signInWithEmail({ email: selected!.email!, password }),
    onSuccess: () => finish(people.orgId),
  });
  const addAnother = useMutation({
    mutationFn: async () => {
      try {
        await signOut();
      } catch (error) {
        if (!(error instanceof ApiError && error.status === 401)) throw error;
      }
    },
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: "/sign-in" });
    },
  });

  function choose(tile: PersonTile) {
    setSelectedKey(tile.key);
    setPin("");
    setPassword("");
    unlock.reset();
    signIn.reset();
    if (
      shared &&
      tile.staffProfileId &&
      shared.ids.includes(tile.staffProfileId)
    )
      unlock.mutate({ pin: shared.pin, staffProfileId: tile.staffProfileId });
    setShared(null);
  }

  function changePin(next: string) {
    setPin(next);
    if (next.length !== PIN_LENGTH || unlock.isPending) return;
    unlock.mutate({
      pin: next,
      ...(selected?.staffProfileId
        ? { staffProfileId: selected.staffProfileId }
        : {}),
    });
  }

  if (people.loading) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <LoadingState label="Opening SafePlate" waking />
      </main>
    );
  }
  if (people.error && !people.noDevice) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <div className="w-full max-w-xl">
          <ErrorState
            message={displayError(people.error)}
            onAction={people.refetch}
            title="Couldn’t open SafePlate"
          />
        </div>
      </main>
    );
  }
  if (!lockScreenAvailable(people.tiles)) {
    return <Navigate replace to="/sign-in" />;
  }

  const busy = unlock.isPending || signIn.isPending;
  const error = unlock.error ?? signIn.error ?? addAnother.error;
  // Typing a PIN before choosing a name picks the person.
  const pinFirst =
    !selected && people.tiles.some((tile) => tile.method === "pin");
  const errorText =
    pinFirst && error instanceof ApiError && error.code === "validation_failed"
      ? "Tap your name"
      : error
        ? displayError(error)
        : null;

  return (
    <main className="min-h-dvh bg-canvas px-5 py-8 sm:py-12">
      <div className="mx-auto max-w-3xl">
        <Brand />
        <header className="mt-10 text-center">
          <h1 className="text-3xl font-black tracking-tight">Who’s logging?</h1>
          {people.orgName ? (
            <p className="mt-2 text-sm font-semibold text-stone-500">
              {people.orgName}
            </p>
          ) : null}
        </header>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {people.tiles.map((tile) => (
            <button
              aria-pressed={selectedKey === tile.key}
              className={cn(
                "min-h-24 rounded-xl border bg-surface p-4 text-left transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800",
                selectedKey === tile.key ||
                  (shared &&
                    tile.staffProfileId &&
                    shared.ids.includes(tile.staffProfileId))
                  ? "border-emerald-800 ring-2 ring-emerald-800/15"
                  : "border-stone-200 hover:border-stone-300",
                shared &&
                  !(
                    tile.staffProfileId &&
                    shared.ids.includes(tile.staffProfileId)
                  ) &&
                  "opacity-50",
              )}
              disabled={busy}
              key={tile.key}
              onClick={() => choose(tile)}
              type="button"
            >
              <span className="grid size-10 place-items-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-900">
                {initialsOf(tile.name)}
              </span>
              <span className="mt-3 block font-bold">{tile.name}</span>
              <span className="mt-1 block text-xs text-stone-500">
                {roleLabels[tile.role]}
              </span>
            </button>
          ))}
        </div>
        {shared ? (
          <p
            className="mt-6 text-center text-sm font-bold text-stone-900"
            role="status"
          >
            Tap your name
          </p>
        ) : selected || pinFirst ? (
          <Card className="mx-auto mt-6 max-w-md">
            <CardContent className="p-6">
              <p className="mb-5 text-center text-sm font-bold">
                {selected ? selected.name : "PIN"}
              </p>
              {!selected || selected.method === "pin" ? (
                <PinPad disabled={busy} onChange={changePin} value={pin} />
              ) : (
                <form
                  onSubmit={(event: FormEvent) => {
                    event.preventDefault();
                    signIn.mutate();
                  }}
                >
                  <Label htmlFor="lock-password">Password</Label>
                  <Input
                    autoComplete="current-password"
                    autoFocus
                    className="mt-2"
                    id="lock-password"
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    type="password"
                    value={password}
                  />
                  <Button
                    className="mt-5 w-full"
                    disabled={password.length === 0 || busy}
                    type="submit"
                  >
                    {signIn.isPending ? "Please wait…" : "Sign in"}
                  </Button>
                </form>
              )}
              {errorText ? (
                <p
                  className="mt-4 text-center text-sm font-semibold text-red-800"
                  role="alert"
                >
                  {errorText}
                </p>
              ) : null}
              <div className="flex justify-center">
                <SlowStartNotice active={busy} />
              </div>
            </CardContent>
          </Card>
        ) : null}
        <div className="mt-6 flex justify-center">
          <Button
            disabled={addAnother.isPending || busy}
            onClick={() => addAnother.mutate()}
            variant="outline"
          >
            <UserPlus className="size-4" aria-hidden="true" /> Add another
            person
          </Button>
        </div>
      </div>
    </main>
  );
}
